import React, { useId } from "react";

import { Input, type InputProps } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";

export type CurrencyInputProps = Omit<InputProps, "prefix"> & {
  label?: string;
  prefix?: boolean | string;
  suffix?: boolean | string;
};

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ label, prefix = true, suffix = true, className, ...props }, ref) => {
    const id = useId();

    // Determine if prefix should be shown and what value to use
    const showPrefix = prefix !== false;
    const prefixValue = typeof prefix === "string" ? prefix : "€";

    // Determine if suffix should be shown and what value to use
    const showSuffix = suffix !== false;
    const suffixValue = typeof suffix === "string" ? suffix : "EUR";

    // Calculate padding based on what's shown
    const paddingClasses = [
      showPrefix && "ps-6",
      showSuffix && "pe-12",
      !showPrefix && !showSuffix && "px-3",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="*:not-first:mt-2">
        {label && <Label htmlFor={id}>{label}</Label>}
        <div className="relative">
          <Input
            {...props}
            ref={ref}
            id={id}
            className={`peer ${paddingClasses} ${className || ""}`}
            type="text"
          />
          {showPrefix && (
            <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-sm text-muted-foreground peer-disabled:opacity-50">
              {prefixValue}
            </span>
          )}
          {showSuffix && (
            <span className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm text-muted-foreground peer-disabled:opacity-50">
              {suffixValue}
            </span>
          )}
        </div>
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export default CurrencyInput;